import { isEmpty } from "../../utils/Util";
import Header from "../../layouts/simple/header";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Toolbar,
  FormControlLabel,
  Switch,
  Box,
  useTheme
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";
import { useLoginMutation } from "../../services/API/authApiSlice";
import { AuthRequest } from "../../types/types";
import { useLocation, useNavigate } from "react-router-dom";
import { setCredentials } from "../../features/auth/authSlice";
import { setIsRemotePayment } from "../../features/dashboard/dashboardSlice";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import {
  DealerProfile,
  GetDealerSettings,
  JWTDecode,
  TargetPayApi,
  GetDealerOnboardedLocationGroups,
  GetDealerPaymentGateway
} from "../../utils/UtilTarget";
import { shouldLoadPageSettings } from "../../features/settings/transactionsettings.slice";
import { setDealerId } from "../../features/dealer/dealerSlice";
import PerseusPay from "../../layouts/dashboard/nav/PerseusPay.png";
import { toBase64 } from "../../common/components/AppUtils";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const [form, setForm] = useState<AuthRequest>({
    dmsDealerId: undefined,
    username: "",
    password: ""
  });

  const [login, { isLoading }] = useLoginMutation();
  const [isRequired, setIsRequired] = useState<any>(true);
  const [isTLDomainUser, setIsTLDomainUser] = useState<any>(false);
  const [userValidated, setUserValidated] = useState<any>(false);
  const [dmsDealerId, setDmsDealerId] = useState<any>("");
  const [imageError, setImageError] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const onboardDealerToken = searchParams.get("onboardDealerToken");
  
  useEffect(() => {
    if (onboardDealerToken) {
    }
  }, [onboardDealerToken]);

  const handleImageError = () => {
    setImageError(true);
  };

  //-------------------- Handlers --------------------//
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Regex to allow only positive integers
    const regex = /^\d*$/;

    if (name === "dmsDealerId") {
      // Check if the input matches the regex
      if (regex.test(value) || value === "") {
        setDmsDealerId(value);

        setForm(prevState => ({
          ...prevState,
          [name]: Number(value)
        }));
      }
    } else {
      setForm(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleSwitchChange = e => {
    setIsRequired(!e.target.checked);
    setIsTLDomainUser(e.target.checked);

    if (e.target.checked)
      setForm(prevState => ({
        ...prevState,
        ["dmsDealerId"]: undefined
      }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Base64-encode password before sending through WAF
    const loginPayload: AuthRequest = {
      ...form,
      password: toBase64(form.password)
    }

    await login(loginPayload)
      .unwrap()
      .then(async response => {
        dispatch(
          setCredentials({
            access_token: response.access_token,
            expires_in: response.expires_in,
            refresh_token: response.refresh_token,
            token_type: response.token_type,
            scope: response.scope
          })
        );
        setUserValidated(true);

        // reset the isRemotePayment global state
        dispatch(setIsRemotePayment(false));
        dispatch(shouldLoadPageSettings(true));

        // set token in localStorage
        let postLogin = localStorage.getItem("postLogin");
        if (isEmpty(postLogin) || postLogin === "/login")
          postLogin = localStorage.getItem("lastpostLogin");

        if (isEmpty(postLogin) || postLogin === "/login") postLogin = "/";

        localStorage.clear();
        localStorage.setItem("access_token", response.access_token);
        localStorage.setItem("refresh_token", response.refresh_token);
        localStorage.setItem("onboardDealer_token", onboardDealerToken);
        localStorage.setItem("isTLDomainUser", isTLDomainUser);

        if (postLogin !== "/login") {
          localStorage.setItem("lastpostLogin", postLogin);
        }

        if (isTLDomainUser) {
          toast.success("Logged in successfully");
          // navigate to PreOnboarding
          setTimeout(() => {
            navigate("/dashboard/preonboarding");
          }, 500);
        } else {
          let dealerId: number = 0;
          let locationGroupId: number = 0;
          let locationGroupName: string = "";

          if (!isEmpty(onboardDealerToken)) {
            dealerId = JWTDecode(onboardDealerToken, "DealerId");
            locationGroupId = Number(
              JWTDecode(onboardDealerToken, "LocationGroupId")
            );
            locationGroupName = JWTDecode(
              onboardDealerToken,
              "LocationGroupName"
            );
            localStorage.setItem(
              "dms_dealer_id",
              JWTDecode(onboardDealerToken, "DmsDealerId2")
            );
          } else {
            dealerId = await DealerProfile("dealerId");

            let everestDmsDealerId = await DealerProfile("everestDmsDealerId");
            if (isEmpty(everestDmsDealerId)) everestDmsDealerId = dmsDealerId;

            localStorage.setItem(
              "dms_dealer_id",
              everestDmsDealerId.toString()
            );

            // find all locations against the Everest DmsDealerId
            var dealerLocationGroups = await GetDealerOnboardedLocationGroups(
              everestDmsDealerId,
              dealerId
            );

            if (dealerLocationGroups && dealerLocationGroups.length > 0) {
              localStorage.setItem(
                "dms_location_groups",
                JSON.stringify(dealerLocationGroups)
              );

              // check 1 -> find Last logged in group
              var defaultOnboarding = dealerLocationGroups.find(
                x => x.isLastLoginGroup
              );

              // check 2 -> find default companyDealer group
              if (!defaultOnboarding)
                defaultOnboarding = dealerLocationGroups.find(
                  x => x.companyDealerId == dealerId
                );

              // check 3 -> pick the first record if 1st and 2nd checks are failed.
              locationGroupId = defaultOnboarding
                ? defaultOnboarding.locationGroupId
                : dealerLocationGroups[0].locationGroupId;
              locationGroupName = defaultOnboarding
                ? defaultOnboarding.name
                : dealerLocationGroups[0].name;
              dealerId = defaultOnboarding
                ? defaultOnboarding.companyDealerId
                : dealerLocationGroups[0].companyDealerId;

              console.log("Default Dealer: ", dealerId);
            }
          }

          if (locationGroupId !== null && locationGroupId !== 0) {
            localStorage.setItem("group_dealerId", dealerId.toString());
            localStorage.setItem(
              "location_group_id",
              locationGroupId.toString()
            );
            localStorage.setItem("location_group_name", locationGroupName);

            dispatch(setDealerId(dealerId.toString()));

            const res = await IsDealerPreOnboarded(dealerId);

            if (res === false) {
              navigate("/401");
            } else {
              await Promise.all([
                GetDealerPaymentGateway(dealerId),
                GetDealerSettings(dealerId)
              ]);
              await GetLocationGroupsAndApply(dealerId);

              toast.success("Logged in successfully");
              // navigate to dashboard
              setTimeout(() => navigate("/"), 500);
            }
          } else {
            const res = await IsDealerPreOnboarded(dealerId);

            if (res === false) {
              navigate("/401");
            } else {
              await Promise.all([
                GetDealerPaymentGateway(dealerId),
                GetDealerSettings(dealerId)
              ]);

              toast.success("Logged in successfully");
              // navigate to dashboard
              setTimeout(() => navigate("/"), 500);
            }
          }
        }
      })
      .catch(error => {
        //TFS:157624
        if (error.status && error.status === 403) {
          toast.error("Access Denied");
        } else {
          toast.error("Invalid Userame or Password");
        }
      });
  };

  async function IsDealerPreOnboarded(dealerId: number) {
    if (isEmpty(dealerId)) {
      return false;
    }

    const res = await TargetPayApi(
      `GetPreOnBoardingLink?dealerId=${dealerId}`,
      null,
      "get"
    );

    if (!isEmpty(res)) return true;
    else return false;
  }

  async function GetLocationGroupsAndApply(dealerId: number) {
    let payment_gateway = localStorage.getItem("payment_gateway");
    let previousLocationGroups = localStorage.getItem("dms_location_groups");
    if (
      payment_gateway &&
      Number(payment_gateway) > 0 &&
      !previousLocationGroups
    ) {
      var onboardingDmsDealerId = localStorage.getItem("dms_dealer_id");
      var dealerLocationGroups = await GetDealerOnboardedLocationGroups(
        Number(onboardingDmsDealerId),
        dealerId
      );
      if (dealerLocationGroups && dealerLocationGroups.length > 0) {
        localStorage.setItem(
          "dms_location_groups",
          JSON.stringify(dealerLocationGroups)
        );
      }
    }
  }

  return (
    <>
      <Helmet>
        <title> Login | {theme.browserTitle} </title>
      </Helmet>

      <Header />
      <Toolbar />
      <Toolbar />

      <Container>
        <Grid container>
          {/* Left Empty Grid */}
          <Grid item xs={12} md={4} />

          {/* Center Grid */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" textAlign={"center"} gutterBottom>
              <img
                src={imageError ? PerseusPay : theme.loginLogo}
                onError={handleImageError}
                style={{ height: "50px", marginBottom: "60px" }}
              />
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                sx={{ mb: "10px" }}
                label="DMS Dealer Id"
                name="dmsDealerId"
                required={isRequired}
                value={dmsDealerId}
                onChange={handleChange}
                style={{
                  visibility: isRequired ? "visible" : "hidden"
                }}
                size="small"
                variant="outlined"
                autoFocus
              />

              <TextField
                fullWidth
                sx={{ mb: "10px" }}
                label="Username"
                name="username"
                required
                value={form.username}
                onChange={handleChange}
                size="small"
                type="text"
                variant="outlined"
              />

              <TextField
                fullWidth
                sx={{ mb: "15px" }}
                label="Password"
                name="password"
                required
                value={form.password}
                onChange={handleChange}
                size="small"
                type="password"
                variant="outlined"
              />

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={isLoading || userValidated}
                endIcon={
                  isLoading || userValidated ? (
                    <CircularProgress size="1.5rem" />
                  ) : null
                }
              >
                <>{isLoading || userValidated ? "Please wait" : "Login"}</>
              </Button>
              <Box style={{ textAlign: "right", marginTop: "10px" }}>
                <FormControlLabel
                  control={<Switch />}
                  label={<span>Login as Internal User</span>}
                  style={{ marginRight: "0px" }}
                  onChange={handleSwitchChange}
                />
              </Box>
            </form>
          </Grid>

          {/* Right Empty Grid */}
          <Grid item xs={12} md={4} />
        </Grid>
      </Container>
    </>
  );
};

export default Login;
